import { z } from 'zod'
import {
  isNameValid,
  isNumberPositiveInteger
} from '@/core/validations/validations.ts'
import { quoteSchema } from '@/modules/sales/clients/types/Quote.ts'
import {
  clientLevelNameSchema,
  clientLevelSchema
} from '@/modules/sales/clients/types/ClientLevel.ts'
import { clientPersonalDataSchema } from '@/modules/sales/clients/types/personalData/PersonalDataDTO.ts'
import { geoAddressSchema } from '@/modules/sales/clients/types/geoAddressSchema.ts'
import { clientAddressSchema } from '@/modules/sales/clients/types/Article.ts'
import { addressSchema } from '@/modules/sales/clients/components/assignArticle/components/supplierAutomotive/assignMotorbikeArticles/assignArticleFormSchema.ts'

export const baseClientSchema = z.object({
  clientName: z.string().nonempty('Requerido'),
  email: z.string().nonempty('Requerido').email('Entre un email correcto'),
  documentNumber: z.string().nonempty('Requerido'),
  phone: z.string().nonempty('Requerido'),
  registerNumber: z.number(),
  createByName: z.string().nonempty('Requerido'),
  credit: z.string().nonempty('Requerido')
})

export const clientSchema = baseClientSchema
  .omit({ registerNumber: true })
  .merge(
    z.object({
      clientId: z.string(),
      creditId: z.string(),
      userClientId: z.string().nullable(),
      isEmailVerify: z.boolean(),
      isBiometricVerify: z.boolean(),
      isPhoneVerify: z.boolean(),
      isMovistarClient: z.boolean().nullable(),
      isZoneUClient: z.boolean().nullable(),
      isLocked: z.boolean(),
      levelId: clientLevelSchema,
      levelName: clientLevelNameSchema
    })
  )

export const clientCreditSchema = z.object({
  creditId: z.string(),
  registerNumber: z.string(),
  quotes: z.array(quoteSchema)
})

export const clientDetailSchema = baseClientSchema
  .omit({
    clientName: true,
    registerNumber: true,
    credit: true
  })
  .merge(
    z.object({
      id: z.string(),
      uuid: z.string(),
      walletId: z.string().nullable(),
      name: z.string(),
      lastName: z.string(),
      birthDate: z.string(),
      photo: z.string().nullable(),
      age: z.string(),
      creditLimit: z.string(),
      referralCode: z.string(),
      credits: z.array(clientCreditSchema),
      isEmailVerify: z.boolean(),
      isPhoneVerify: z.boolean(),
      isBiometricVerify: z.boolean(),
      isMovistarClient: z.boolean().nullable(),
      isZoneUClient: z.boolean().nullable(),
      levelId: clientLevelSchema,
      levelName: clientLevelNameSchema,
      levelRechargeDiscount: z.string(),
      levelActiveDevices: z.number(),
      accountPhone: z.string(),
      creditLimitMiscellaneous: z.string(),
      rentabilityId: z.string(),
      rentabilityQuotes: z.string(),
      rentabilityExchange: z.string(),
      rentabilityTimesQuotes: z.string(),
      rentabilityIsMain: z.boolean(),
      initialByLevel: z.string(),
      addresses: z.array(geoAddressSchema).optional(),
      clientAddress: clientAddressSchema.nullable().optional(),
      workAddress: clientAddressSchema.nullable().optional()
    })
  )
  .merge(clientPersonalDataSchema)

export const clientDtoSchema = z.object({
  name: z
    .string()
    .nonempty('Requerido')
    .max(50, 'No debe exceder los 50 caracteres')
    .refine((val) => isNameValid(val), {
      message: 'Solo puede contener letras y espacios'
    }),
  lastName: z
    .string()
    .nonempty('Requerido')
    .max(50, 'No debe exceder los 50 caracteres')
    .refine((val) => isNameValid(val), {
      message: 'Solo puede contener letras y espacios'
    }),
  birthDate: z.string().nonempty('Requerido'),
  documentNumber: z
    .string()
    .nonempty('Requerido')
    .min(7, 'Debe tener al menos 7 dígitos')
    .max(8, 'Debe tener máximo 8 dígitos')
    .refine((val) => /^[^0]/.test(val), { message: 'No debe empezar con cero' })
    .refine((val) => isNumberPositiveInteger(val), {
      message: 'Deben ser caracteres numéricos'
    }),
  personalData: clientPersonalDataSchema,
  addresses: z.array(addressSchema).optional()
})

export const clientEditDtoSchema = clientDtoSchema.merge(
  z.object({
    email: z
      .string()
      .email('Ingrese un correo válido (ej: usuario@dominio.com)'),
    creditLimit: z.string(),
    phone: z.string(),
    createByName: z.string(),
    documentImage: z.string().nullable()
  })
)

export type Client = z.infer<typeof clientSchema>
export type ClientDTO = z.infer<typeof clientDtoSchema>
export type ClientDetail = z.infer<typeof clientDetailSchema>
export type ClientEditDTO = z.infer<typeof clientEditDtoSchema>
